import React, { useState, useEffect } from 'react';
import './App.css';
import io from 'socket.io-client';

const socket = io('https://host.sandalwoodstays.com');

const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? 'https://host.sandalwoodstays.com'
  : 'http://localhost:5006';

function App() {
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [threadId, setThreadId] = useState('');
  const [recipientId, setRecipientId] = useState('');
  const [bookings, setBookings] = useState([]);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    // Check URL parameters for OAuth success
    const params = new URLSearchParams(window.location.search);
    const oauthSuccess = params.get('oauth');
    
    if (oauthSuccess === 'success') {
      setIsConnected(true);
      // Remove the parameters from URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Check OAuth connection status
    checkOAuthStatus();
    // Check subscription status
    checkSubscriptionStatus();
    // Fetch bookings and threads when component mounts
    fetchBookings();
    fetchThreads();

    socket.on('webhookEvent', (event) => {
      console.log('Received webhook event:', event);
      setWebhookEvents((prev) => [...prev, event]);
    });

    // Listen for new messages
    socket.on('newMessage', message => {
      if (message.thread_id === selectedThread?.threadId) {
        setMessages(prev => [...prev, message]);
      }
    });

    // Listen for thread updates
    socket.on('threadUpdate', thread => {
      setThreads(prev => prev.map(t => t.threadId === thread.id ? thread : t));
    });

    return () => {
      socket.off('webhookEvent');
      socket.off('newMessage');
      socket.off('threadUpdate');
    };
  }, [selectedThread]);

  const checkOAuthStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/oauth/status`);
      const data = await response.json();
      setIsConnected(data.isConnected);
    } catch (err) {
      console.error('Error checking OAuth status:', err);
    }
  };

  const checkSubscriptionStatus = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/webhook/status`);
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || 'Failed to check subscription status');
      }
      
      const data = await response.json();
      setIsSubscribed(data.isSubscribed);
      setError(null);
    } catch (err) {
      console.error('Error checking subscription status:', err);
      setError(err.message);
    }
  };

  const handleOAuthConnect = () => {
    window.location.href = `${API_BASE_URL}/api/oauth/authorize`;
  };

  const handleSubscribe = async () => {
    try {
      setSubscriptionLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE_URL}/api/webhook/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details?.message || errorData.error || 'Failed to subscribe to messages');
      }
      
      const data = await response.json();
      setIsSubscribed(true);
      setError(null);
    } catch (err) {
      console.error('Error subscribing to messages:', err);
      setError(err.message || 'Failed to subscribe to messages');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const fetchBookings = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/bookings`);
      if (!response.ok) throw new Error('Failed to fetch bookings');
      const data = await response.json();
      setBookings(data);
    } catch (err) {
      console.error('Error fetching bookings:', err);
      setError(err.message);
    }
  };

  const fetchThreads = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/threads`);
      if (!response.ok) throw new Error('Failed to fetch threads');
      const data = await response.json();
      setThreads(data);
    } catch (err) {
      console.error('Error fetching threads:', err);
      setError(err.message);
    }
  };

  const handleThreadSelect = async (thread) => {
    try {
      setSelectedThread(thread);
      setThreadId(thread.id);
      setRecipientId(thread.recipient_id);
      setLoading(true);
      
      const response = await fetch(`${API_BASE_URL}/api/threads/${thread.id}/messages`);
      if (!response.ok) throw new Error('Failed to fetch messages');
      
      const data = await response.json();
      setMessages(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !threadId || !recipientId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          threadId,
          content: newMessage,
          recipientId,
        }),
      });

      if (!response.ok) throw new Error('Failed to send message');
      
      const sentMessage = await response.json();
      setMessages(prev => [...prev, sentMessage]);
      setNewMessage('');
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err.message);
    }
  };

  return (
    <div className="App">
      <header className="App-header">
        <h1>OwnerRez Messenger</h1>
        <div className="oauth-section">
          {!isConnected ? (
            <button 
              className="connect-button"
              onClick={handleOAuthConnect}
            >
              Connect to OwnerRez
            </button>
          ) : (
            <div className="connection-status">
              <span className="status-indicator connected"></span>
              Connected to OwnerRez
              {!isSubscribed && (
                <button 
                  className="subscribe-button"
                  onClick={handleSubscribe}
                  disabled={subscriptionLoading}
                >
                  {subscriptionLoading ? 'Subscribing...' : 'Subscribe to Messages'}
                </button>
              )}
              {isSubscribed && (
                <span className="subscription-status">
                  <span className="status-indicator connected"></span>
                  Subscribed to Messages
                </span>
              )}
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      
      <div className="App-container">
        <div className="sidebar">
          <div className="section">
            <h2>Bookings</h2>
            <div className="list">
              {bookings.map(booking => (
                <div key={booking.id} className="list-item">
                  <h3>{booking.property_name}</h3>
                  <p>Check-in: {new Date(booking.check_in).toLocaleDateString()}</p>
                  <p>Check-out: {new Date(booking.check_out).toLocaleDateString()}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="section">
            <h2>Message Threads</h2>
            <div className="list">
              {threads.map(thread => (
                <div 
                  key={thread.id} 
                  className={`list-item ${selectedThread?.id === thread.id ? 'selected' : ''}`}
                  onClick={() => handleThreadSelect(thread)}
                >
                  <h3>{thread.subject || 'No Subject'}</h3>
                  <p>Last message: {new Date(thread.last_message_at).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="main-content">
          {selectedThread ? (
            <>
              <div className="messages-container">
                {loading ? (
                  <div className="loading">Loading messages...</div>
                ) : (
                  messages.map(message => (
                    <div key={message.id} className={`message ${message.is_outbound ? 'outbound' : 'inbound'}`}>
                      <div className="message-content">{message.content}</div>
                      <div className="message-time">
                        {new Date(message.created_at).toLocaleString()}
                      </div>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={handleSendMessage} className="message-form">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="message-input"
                />
                <button type="submit" className="send-button">Send</button>
              </form>
            </>
          ) : (
            <div className="no-thread-selected">
              Select a thread to view messages
            </div>
          )}
        </div>
      </div>

      <div>
        <h2>Webhook Events</h2>
        <ul>
          {webhookEvents.map((event, index) => (
            <li key={index}>{JSON.stringify(event)}</li>
          ))}
        </ul>
      </div>

      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}
    </div>
  );
}

export default App; 